import { ContextMenuHandler } from "@asup/context-menu";
import { ReactNode, useMemo, useState } from "react";
import styles from "../aic.module.css";
import { MosaicTrafficLightClass } from "../traffic-lights/MosaicTrafficLightClass";
import { classMerge } from "../utility/classMerge";
import { AddLineButton } from "./AddLineButton";
import { AibLineTrafficLight } from "./AibLineTrafficLight";
import { AibOptionsWindow } from "./AibOptionsWindow";
import { useBlockContext } from "./BlockContentProvider";
import { ADD_LINE, REMOVE_LINE } from "./blockReducer";
import { CellEditor } from "./CellEditor";
import { AibLineType } from "./interface";
import { newBlockLine } from "./newBlockLine";
import { RemoveLineButton } from "./RemoveLineButton";

export interface AibLineDisplayProps {
  aifid: string;
}

/**
 * Displays a line within the AIB block, including its cells and options.
 *
 * @param props - Destructured parameter object
 * @param props.aifid - Identifier for the block line used to look up line state
 */
export const AibLineDisplay = <T,>({ aifid }: AibLineDisplayProps): ReactNode => {
  const { state, dispatch } = useBlockContext<T>();
  const ix = state.lines.findIndex((l) => l.aifid === aifid);
  const thisLine = ix > -1 ? state.lines[ix] : null;
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const displayType = thisLine?.lineType;
  const isFirstLine = ix === 0;
  const canAddLineAbove =
    isFirstLine && state.canAddFirstRow && state.lines.length < state.maxLines && !state.disabled;
  const canAddLineBelow =
    !state.disabled && state.lines.length < state.maxLines && thisLine?.addBelow;

  const divClassName = [
    styles.aibLineItem,
    displayType && [AibLineType.leftOnly, AibLineType.centerOnly].includes(displayType)
      ? styles.one
      : displayType && [AibLineType.leftAndRight].includes(displayType)
        ? styles.two
        : styles.three,
  ]
    .filter((c) => c !== "")
    .join(" ");

  const lineCoreTrafficLight = useMemo(() => {
    if (
      state &&
      !state.noTrafficLights &&
      thisLine &&
      thisLine.coreTrafficLight &&
      thisLine.coreTrafficLight !== "BlankNotEvaluated"
    ) {
      const tl = new MosaicTrafficLightClass(thisLine.coreTrafficLight, "-4px", "-8px");
      return tl.element;
    } else return null;
  }, [state, thisLine]);

  return !thisLine || !displayType ? null : (
    <div
      id={`${state.id}-${ix}`}
      className={styles.aibLine}
    >
      <div className={styles.aibLineButtons} />
      <AibOptionsWindow
        aifid={aifid}
        showWindow={showOptions}
        onClose={() => setShowOptions(false)}
      />
      <ContextMenuHandler
        className={styles.calcSize}
        menuItems={[
          {
            label: "Show line options",
            action: () => {
              const focusDummy = document.createElement("input");
              document.body.appendChild(focusDummy);
              focusDummy.focus();
              focusDummy.remove();
              setShowOptions(true);
            },
          },
          {
            label: "Insert line",
            group: [
              {
                label: "Above",
                action: () => {
                  const line = newBlockLine(state.defaultType, state.editorProps.blankT);
                  line.canChangeType = state.canChangeType;
                  dispatch({ operation: ADD_LINE, ix: ix, line });
                },
                disabled: !canAddLineAbove,
              },
              {
                label: "Below",
                action: () => {
                  const line = newBlockLine(state.defaultType, state.editorProps.blankT);
                  line.canChangeType = state.canChangeType;
                  dispatch({ operation: ADD_LINE, ix: ix + 1, line });
                },
                disabled: !canAddLineBelow,
              },
            ],
          },
          {
            label: "Delete line",
            action: () => {
              dispatch({ operation: REMOVE_LINE, aifid });
            },
            disabled: state.lines.length <= state.minLines || !thisLine.canRemove,
          },
        ]}
      >
        <div
          className={styles.aibLineItemHolder}
          style={{ ...state.lineStyle }}
        >
          {lineCoreTrafficLight}
          {[
            AibLineType.leftOnly,
            AibLineType.leftAndRight,
            AibLineType.leftCenterAndRight,
          ].includes(displayType) && (
            <div className={divClassName}>
              <CellEditor
                aifid={aifid}
                position="left"
              />
            </div>
          )}
          {[AibLineType.centerOnly, AibLineType.leftCenterAndRight].includes(displayType) && (
            <div className={classMerge([divClassName, styles.flexGrow1])}>
              <CellEditor
                aifid={aifid}
                position="center"
              />
            </div>
          )}
          {[AibLineType.leftAndRight, AibLineType.leftCenterAndRight].includes(displayType) && (
            <div className={divClassName}>
              <CellEditor
                aifid={aifid}
                position="right"
              />
            </div>
          )}
        </div>
      </ContextMenuHandler>

      <div className={styles.aibLineButtons}>
        <AddLineButton aifid={aifid} />
        <RemoveLineButton aifid={aifid} />
        {canAddLineAbove && (
          <AddLineButton
            aifid={aifid}
            lineAbove={true}
          />
        )}
      </div>
      <AibLineTrafficLight aifid={aifid} />
      {ix === state.lines.length - 1 && (
        <AibLineTrafficLight
          aifid="final"
          top={"20px"}
        />
      )}
    </div>
  );
};

AibLineDisplay.displayName = "AibLineDisplay";
